import React, { useEffect, useRef, useState } from "react"
import styles from "./AppSpace.module.scss"
import { UserInterface } from "../../App"
import axios from "axios"
import { API_ROUTE, getFileColor } from "../../utils"
import { Editor } from "@tinymce/tinymce-react"
import toast from "react-hot-toast"
import Sheet from "./Sheet/Sheet"
import PDFViewer from "./PDFViewer/PDFViewer"

const AppSpace: React.FC<{
    setRawContext: any
    activeAppSpace: any
    setActiveAppSpace: any
    appSpaces: any
    setAppSpaces?: any
    user: UserInterface
    lightMode?: boolean
    orgWikis?: any
}> = ({
    setRawContext,
    activeAppSpace,
    setActiveAppSpace,
    appSpaces,
    setAppSpaces,
    user,
    lightMode,
    orgWikis,
}) => {
    const [appSpaceContent, setAppSpaceContent] = useState("" as string | object)
    const [type, setType] = useState("")
    const [appSpaceLoading, setAppSpaceLoading] = useState(false)

    const editorRef = useRef<any>(null)
    const saveTimeoutRef = useRef<NodeJS.Timeout>()

    const isValidBase64 = (str: string) => {
        try {
            return btoa(atob(str)) === str
        } catch {
            return false
        }
    }

    const saveChanges = async (newContent: string, contentType: string) => {
        if (!newContent || newContent.trim() === "") {
            toast.error("Cannot save empty content")
            return
        }

        try {
            // Validate HTML content, skip this part if dealing with Sheets (application/json)
            const parser = new DOMParser()
            const doc = parser.parseFromString(newContent, "text/html")
            if (doc.querySelector("parsererror")) {
                throw new Error("Invalid HTML content")
            }

            // Encode content to Base64
            const base64Content = btoa(unescape(encodeURIComponent(newContent)))
            const encodedContent = `data:text/html;base64,${base64Content}`

            // Verify Base64
            if (!isValidBase64(base64Content)) {
                throw new Error("Invalid Base64 encoding")
            }

            console.log("Saving content:", newContent)
            console.log("Encoded content:", encodedContent)

            const response = await axios.post(`${API_ROUTE}/files/update-file`, {
                fileId: activeAppSpace.fileId,
                content: encodedContent,
                contentType: contentType === "text" ? "text/html" : "application/json",
            })

            if (response.data.success) {
                toast.success("Document saved successfully")
            } else {
                throw new Error(response.data.message || "Save failed")
            }
        } catch (error) {
            console.error("Save changes error:", error)
            toast.error(`Failed to save document`)
        }
    }

    const debouncedSaveChanges = (content: string) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current)
        }
        saveTimeoutRef.current = setTimeout(() => {
            saveChanges(content, type)
        }, 800)
    }

    useEffect(() => {
        ;(async () => {
            if (!activeAppSpace.fileId || !activeAppSpace.contentType) {
                setAppSpaceLoading(false)
                return
            }
            setAppSpaceLoading(true)
            setType("none")

            await new Promise((resolve) => setTimeout(resolve, 200))

            try {
                const response = await axios.get(
                    `${API_ROUTE}/files/get-file/${activeAppSpace.fileId}`
                )
                const dataUrl = response.data.content

                console.log("Loaded file data:", {
                    fileId: activeAppSpace.fileId,
                    contentType: activeAppSpace.contentType,
                    dataUrl,
                })

                if (activeAppSpace.contentType === "application/pdf") {
                    const text = await axios.post(`${API_ROUTE}/utils/pdf`, { url: dataUrl })
                    setRawContext(text.data)
                    setAppSpaceContent(dataUrl)
                    setType("pdf")
                    setAppSpaceLoading(false)
                    return
                } else if (activeAppSpace.contentType.startsWith("image/")) {
                    setRawContext(dataUrl)
                    setAppSpaceContent(dataUrl)
                    setType("image")
                    setAppSpaceLoading(false)
                    return
                } else {
                    try {
                        // Handle any data URL prefix
                        const base64Data = dataUrl.replace(/^data:[^;]+;base64,/, "")
                        if (!isValidBase64(base64Data)) {
                            throw new Error("Invalid Base64 content in stored file")
                        }
                        const decodedContent = decodeURIComponent(escape(atob(base64Data)))

                        switch (activeAppSpace.contentType) {
                            case "text/html":
                                setRawContext(decodedContent)
                                setAppSpaceContent(decodedContent)
                                setType("text")
                                break
                            case "application/json":
                                const parsed = JSON.parse(decodedContent)
                                const parsedContent = parsed[Object.keys(parsed)[0]] // only supporting first sheet for now

                                // a readable format so GPT can understand it
                                setRawContext(JSON.stringify(parsedContent, null, 2))
                                setAppSpaceContent(parsedContent)
                                setType("sheet")
                                break
                            default:
                                setRawContext(decodedContent)
                                setAppSpaceContent(decodedContent)
                                setType("text")
                                break
                        }
                        setAppSpaceLoading(false)
                    } catch (error) {
                        console.error("Failed to decode document content:", error)
                        setAppSpaceContent("")
                        toast.error("Failed to load document content")
                    }
                }
            } catch (error) {
                console.error("Failed to load file:", error)
                setAppSpaceLoading(false)
                toast.error("Failed to load file")
            }
        })()
    }, [activeAppSpace, appSpaces, setRawContext])

    const handleEditorChange = () => {
        if (editorRef.current) {
            const content = editorRef.current.getContent()
            setRawContext(content)
            debouncedSaveChanges(content)
        }
    }

    useEffect(() => {
        // re-render the app space when light mode changes
        const appSpace = activeAppSpace
        setActiveAppSpace({})

        setTimeout(() => {
            setActiveAppSpace(appSpace)
        }, 200)
    }, [lightMode])

    const openWikiPage = async (pageId: string) => {
        try {
            const { data: file } = await axios.get(`${API_ROUTE}/files/get-file/${pageId}`)
            if (!file || !file._id) {
                toast.error("Wiki page not found")
                return
            }
            setActiveAppSpace({
                ...file,
                fileId: file._id,
                content: file.content,
                contentType: file.contentType,
                type: "wikipage",
                name: file.name || file.title || "Wiki Page",
            })
        } catch (err) {
            console.error("Error opening wiki page:", err)
            toast.error("Failed to open wiki page")
        }
    }

    return (
        <div
            style={{
                display: appSpaces?.length > 0 ? undefined : "none",
                padding: activeAppSpace?.type === "wikipage" ? "0 1rem 1rem 0" : "0",
                background: activeAppSpace?.type === "wikipage" ? "transparent" : "var(--shade-3)",
            }}
            className={styles.AppSpace}>
            {appSpaceLoading && (
                <div className={styles.loading}>
                    <div className="loader"></div>
                    Loading file...
                </div>
            )}

            <div className={styles.header}>
                <div>
                    {activeAppSpace?.type !== "wikipage" && <p>FILE EDITOR</p>}

                    <div className="flex">
                        {activeAppSpace?.type !== "wikipage" && (
                            <div
                                style={{
                                    background: getFileColor(activeAppSpace.contentType),
                                }}
                                className={styles.fileType}></div>
                        )}
                        {activeAppSpace.name}
                    </div>
                </div>

                <div
                    className={styles.close}
                    onClick={() => {
                        setActiveAppSpace({})
                        setAppSpaces([])
                    }}>
                    <i className="material-symbols-rounded">close</i>
                </div>
            </div>

            {type === "pdf" && (
                // <iframe
                //     src={appSpaceContent as string}
                //     onLoad={() => setAppSpaceLoading(false)}
                //     width={"100%"}
                //     height={"100%"}
                //     title="pdf"
                //     style={{ border: "none" }}
                // />
                <PDFViewer dataUrl={appSpaceContent as string} />
            )}
            {type === "image" && (
                <div className={styles.imageViewer}>
                    <img
                        src={appSpaceContent as string}
                        onLoad={() => setAppSpaceLoading(false)}
                        onError={() => setAppSpaceLoading(false)}
                        style={{ width: "100%", height: "auto" }}
                        alt="viewer"
                    />
                </div>
            )}
            {type === "sheet" && <Sheet sheetContent={appSpaceContent as object} />}
            {type === "text" && (
                <div style={{ height: "100%" }}>
                    <Editor
                        onInit={(evt: any, editor: any) => {
                            editorRef.current = editor

                            editor.dom.addStyle(`
                                .wiki-ref {
                                  display: inline-block;
                                  padding: 0 8px;
                                  border-radius: 6px;
                                  border: 1px solid #ffb40020;
                                  background: #ffb40010;
                                  color: #ffb400;
                                  font-weight: 500;
                                  cursor: pointer;
                                  white-space: nowrap;
                                  line-height: 1.6;
                                }
                                .wiki-ref:hover {
                                  background: rgba(90, 110, 255, 0.26);
                                  border-color: rgba(90, 110, 255, 0.65);
                                }
                                .mce-content-body .wiki-ref.mce-selected {
                                  outline: 2px solid rgba(90,110,255,0.55);
                                  outline-offset: 1px;
                                }
                              `)
                        }}
                        onLoadContent={() => {
                            setAppSpaceLoading(false)
                        }}
                        initialValue={appSpaceContent as string}
                        apiKey="0zy4qv4meksyt7oin8lzvenoxzv12sgxfb1fwhnedck1neff"
                        // apiKey="tfd42flx51qvoat765iks55w1x8vbhb0l0pcyizle74z6ki5"
                        onChange={handleEditorChange}
                        init={{
                            height: "100%",
                            menubar: "edit view insert format tools",
                            skin: lightMode ? "oxide" : "oxide-dark",
                            content_css: lightMode ? "default" : "dark",
                            content_style:
                                "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",

                            toolbar:
                                "undo redo | formatselect | " +
                                "bold italic backcolor | alignleft aligncenter " +
                                "alignright alignjustify | bullist numlist outdent indent | " +
                                "removeformat",

                            setup: (editor) => {
                                // --- util: escape titles so we don't inject raw HTML ---
                                const escapeHtml = (s: string) =>
                                    s
                                        .replace(/&/g, "&amp;")
                                        .replace(/</g, "&lt;")
                                        .replace(/>/g, "&gt;")
                                        .replace(/"/g, "&quot;")
                                        .replace(/'/g, "&#039;")

                                // --- source: your org's wiki pages only ---
                                const wikiPages: Array<{
                                    _id: string
                                    title: string
                                }> = Object.values(
                                    (orgWikis?.pages as Record<
                                        string,
                                        { _id: string; title: string }
                                    >) ?? {}
                                )

                                // --- autocompleter: type # then filter titles ---
                                editor.ui.registry.addAutocompleter("articleLinks", {
                                    ch: "#",
                                    minChars: 1,
                                    columns: 1,
                                    highlightOn: ["text"],

                                    onAction: (autocompleteApi, rng, tokenHtml) => {
                                        editor.selection.setRng(rng)
                                        editor.insertContent(tokenHtml + "&nbsp;") // space after chip prevents #→H1
                                        autocompleteApi.hide()
                                    },

                                    // proper TinyMCE typing: (pattern, maxResults, fetchOptions)
                                    fetch: (
                                        pattern: string,
                                        maxResults: number,
                                        _fetchOptions: Record<string, any>
                                    ) => {
                                        const q = pattern.trim().toLowerCase()

                                        const matches = wikiPages
                                            .filter((p) => !q || p.title.toLowerCase().includes(q))
                                            .slice(0, Math.max(1, Math.min(maxResults || 20, 50)))

                                        return Promise.resolve(
                                            matches.map((p) => {
                                                const label = `#${p.title}`
                                                const tokenHtml =
                                                    `<span class="wiki-ref mceNonEditable" contenteditable="false" ` +
                                                    `data-wiki-id="${
                                                        p._id
                                                    }" title="Ctrl/⌘+Click or Double-click to open">${escapeHtml(
                                                        label
                                                    )}</span>`

                                                return {
                                                    type: "autocompleteitem" as const,
                                                    value: tokenHtml,
                                                    text: p.title,
                                                }
                                            })
                                        )
                                    },
                                })

                                editor.on("click", (e: any) => {
                                    const el = (e.target as HTMLElement)?.closest(
                                        ".wiki-ref"
                                    ) as HTMLElement | null
                                    if (!el) return

                                    if (e.ctrlKey || e.metaKey) {
                                        e.preventDefault()
                                        const id = el.getAttribute("data-wiki-id")
                                        if (id) void openWikiPage(id)
                                        return
                                    }
                                    e.preventDefault()
                                    editor.selection.select(el)
                                })

                                editor.on("dblclick", (e: any) => {
                                    const el = (e.target as HTMLElement)?.closest(
                                        ".wiki-ref"
                                    ) as HTMLElement | null
                                    if (!el) return
                                    e.preventDefault()
                                    const id = el.getAttribute("data-wiki-id")
                                    if (id) void openWikiPage(id)
                                })

                                const isWikiChip = (n: Node | null) =>
                                    !!(
                                        n &&
                                        (n as HTMLElement).nodeType === 1 &&
                                        (n as HTMLElement).classList?.contains("wiki-ref")
                                    )

                                editor.on("keydown", (e: KeyboardEvent) => {
                                    if (e.key === "Enter") {
                                        const node = editor.selection.getNode() as HTMLElement
                                        const chip = (node?.closest &&
                                            node.closest(".wiki-ref")) as HTMLElement | null

                                        // HARD BLOCK: if the line *starts* with a wiki chip, don’t let Enter accidentally apply any pattern/format
                                        if (chip) {
                                            e.preventDefault()
                                            const id = chip.getAttribute("data-wiki-id")
                                            if (id) void openWikiPage(id)
                                            return
                                        }

                                        // Also block any textpattern-style transforms when the paragraph begins with '#'
                                        const block = editor.dom.getParent(
                                            editor.selection.getStart(),
                                            editor.dom.isBlock as any
                                        ) as HTMLElement | null
                                        if (block && /^\s*#/.test(block.innerText)) {
                                            e.preventDefault()
                                            // Force a plain newline instead
                                            editor.execCommand("InsertLineBreak")
                                            return
                                        }
                                    }

                                    if (e.key === "Backspace" || e.key === "Delete") {
                                        const rng = editor.selection.getRng()
                                        const container = rng.startContainer
                                        const offset = rng.startOffset

                                        const node = (
                                            container.nodeType === 3
                                                ? container.parentNode
                                                : container
                                        ) as Node | null
                                        if (!node) return

                                        if (e.key === "Backspace") {
                                            // if caret is just after a chip
                                            const prev = (node.childNodes?.[
                                                Math.max(0, offset - 1)
                                            ] || node.previousSibling) as Node | null
                                            if (isWikiChip(prev)) {
                                                e.preventDefault()
                                                ;(prev as HTMLElement).remove()
                                                return
                                            }
                                        } else {
                                            // Delete: if caret is just before a chip
                                            const next = (node.childNodes?.[offset] ||
                                                node.nextSibling) as Node | null
                                            if (isWikiChip(next)) {
                                                e.preventDefault()
                                                ;(next as HTMLElement).remove()
                                                return
                                            }
                                        }
                                    }
                                })
                            },
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default AppSpace
